import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import type { Word, StageConfigEntry } from '../../types';
import { api } from '../../services/api';
import { useQuickApproveStore } from '../../stores/quickApproveStore';
import { useUiStore } from '../../stores/uiStore';
import ContentCard from '../ContentCard';
import RelatedSlotActions from './RelatedSlotActions';
import ApproveButton from '../ApproveButton';
import CompactToggle from '../CompactToggle';
import type { GenStats } from '../../pages/GameWorkspace';
import './StageCommon.css';

interface FieldStats {
  field: string;
  tokens: number;
  elapsedSec: number;
}

interface ReviewStageProps {
  /** Called after a related-word slot is refilled, so the parent can reload. */
  onSlotChanged?: () => void;
  word: Word;
  config: StageConfigEntry;
  taskName?: string;
  onGenerate: () => void;
  onApprove: (selections: Record<string, unknown>) => void;
  onEdit?: (fieldId: string, newContent: string) => void;
  onRegenerateField?: (fieldName: string, instructions?: string) => Promise<{ value: string; model: string | null; tokens: number; elapsedSec: number } | null>;
  lastGenStats?: GenStats | null;
  generating: boolean;
  saving: boolean;
  registerApprovePayload: (fn: (() => Record<string, unknown> | null) | null) => void;
}

function formatTokens(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k tokens`;
  return `${n} tokens`;
}

export default function ReviewStage({
  onSlotChanged, word, config, taskName, onGenerate, onApprove, onEdit, onRegenerateField, lastGenStats, generating, saving, registerApprovePayload }: ReviewStageProps) {
  const [compareData, setCompareData] = useState<Record<string, string> | null>(null);
  const [compareModel, setCompareModel] = useState<string | null>(null);
  const [regeneratingField, setRegeneratingField] = useState<string | null>(null);
  const [lastFieldStats, setLastFieldStats] = useState<FieldStats | null>(null);
  const [lintFindings, setLintFindings] = useState<Array<{ field: string; type: string; severity: string; message: string; match?: string }>>([]);
  // Synchronous in-flight guard: React state updates are async, so the button's
  // `disabled` prop can lag by a tick on rapid double-clicks. This ref flips
  // synchronously inside the click handler and is checked before the API call.
  const regenInFlightRef = useRef(false);
  const fieldStatsTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (fieldStatsTimerRef.current) clearTimeout(fieldStatsTimerRef.current);
    };
  }, []);

  // Toolbar shows the session's latest generation stats when available; falls
  // back to the persisted `generated_by_model` on the word record.
  const persistedModel = typeof word.generated_by_model === 'string' ? word.generated_by_model : null;
  const toolbarModel = lastGenStats?.model || persistedModel;

  const { isQuickApproveOn, toggleQuickApprove, shouldShowNudge, dismissNudge } = useQuickApproveStore();
  const compactCards = useUiStore((s) => s.compactCards);
  const qaEnabled = taskName ? isQuickApproveOn(taskName) : false;
  const showNudge = taskName ? shouldShowNudge(taskName) : false;

  const hasContent = (config.fieldGroups || []).some((group) =>
    group.fields.some((f) => word[f])
  );

  useEffect(() => {
    if (!registerApprovePayload) return;
    registerApprovePayload(() => (hasContent ? {} : null));
  }, [registerApprovePayload, hasContent]);

  // Review-time QA lint: flag banned phrases, definitional openings, and
  // anti-anchoring leaks in the current stage's fields. Runs on stage entry and
  // after edits/generation (word changes on blur, not per keystroke). Never
  // hard-blocks — em dashes are intentionally not flagged.
  useEffect(() => {
    if (!hasContent) { setLintFindings([]); return; }
    const fields: Record<string, string> = {};
    for (const group of config.fieldGroups || []) {
      for (const f of group.fields) {
        const v = word[f];
        if (typeof v === 'string' && v.trim()) fields[f] = v;
      }
    }
    if (Object.keys(fields).length === 0) { setLintFindings([]); return; }
    // Debounce the lint POST (2026-07-05 L24 fix): `word` is a fresh object on
    // every identity change, so a stage-1 generate that mutates ~20 fields used
    // to fire ~20 lint POSTs in a burst. Wait out the churn, then send one.
    let cancelled = false;
    const t = setTimeout(() => {
      api.post('/api/qa/lint', { fields })
        .then((res) => { if (!cancelled) setLintFindings((res as { findings?: typeof lintFindings }).findings || []); })
        .catch(() => { if (!cancelled) setLintFindings([]); });
    }, 500);
    return () => { cancelled = true; clearTimeout(t); };
  }, [word, hasContent, config.fieldGroups]);

  const handleEdit = useCallback((fieldId: string, newContent: string) => {
    if (onEdit) onEdit(fieldId, newContent);
  }, [onEdit]);

  // Entering compare mode does NOT auto-regenerate. It opens the compare view
  // with the New column empty; the user populates it with per-field Regenerate.
  const handleCompare = useCallback(() => {
    setCompareData({});
    setCompareModel(null);
  }, []);

  const handleDismissCompare = useCallback(() => {
    setCompareData(null);
    setCompareModel(null);
  }, []);

  const handleUseNew = useCallback((fieldId: string) => {
    if (compareData && compareData[fieldId] && onEdit) {
      onEdit(fieldId, compareData[fieldId]);
    }
  }, [compareData, onEdit]);

  const readOnlySet = useMemo(
    () => new Set(config.readOnlyFields || []),
    [config.readOnlyFields]
  );

  const handleAcceptAllNew = useCallback(() => {
    if (!compareData || !onEdit) return;
    for (const group of (config.fieldGroups || [])) {
      for (const field of group.fields) {
        if (readOnlySet.has(field)) continue;
        if (compareData[field]) {
          onEdit(field, compareData[field]);
        }
      }
    }
  }, [compareData, onEdit, config.fieldGroups, readOnlySet]);

  const handleEditCompareField = useCallback((fieldId: string, newContent: string) => {
    setCompareData((prev) => (prev ? { ...prev, [fieldId]: newContent } : prev));
  }, []);

  const handleRegenerateOne = useCallback(async (fieldId: string, instructions?: string) => {
    if (!onRegenerateField || readOnlySet.has(fieldId)) return;
    if (regenInFlightRef.current) return;
    regenInFlightRef.current = true;
    setRegeneratingField(fieldId);
    try {
      const res = await onRegenerateField(fieldId, instructions);
      if (res) {
        setCompareData((prev) => (prev ? { ...prev, [fieldId]: res.value } : { [fieldId]: res.value }));
        if (res.model) setCompareModel(res.model);
      }
    } finally {
      setRegeneratingField(null);
      regenInFlightRef.current = false;
    }
  }, [onRegenerateField, readOnlySet]);

  // One-level regen undo (session-only, per word): the value a field held
  // before its last regeneration. Set only by an actual regen — never by the
  // generate flow.
  const [undoValues, setUndoValues] = useState<Record<string, string>>({});
  useEffect(() => { setUndoValues({}); }, [word.id]);

  const handleRegenerateInline = useCallback(async (fieldId: string, instructions?: string) => {
    if (!onRegenerateField || readOnlySet.has(fieldId)) return;
    if (regenInFlightRef.current) return;
    regenInFlightRef.current = true;
    const prev = String(word[fieldId] ?? '');
    setRegeneratingField(fieldId);
    try {
      const res = await onRegenerateField(fieldId, instructions);
      if (res) {
        if (prev.trim()) setUndoValues((u) => ({ ...u, [fieldId]: prev }));
        if (onEdit) onEdit(fieldId, res.value);
        setLastFieldStats({ field: fieldId, tokens: res.tokens, elapsedSec: res.elapsedSec });
        if (fieldStatsTimerRef.current) clearTimeout(fieldStatsTimerRef.current);
        fieldStatsTimerRef.current = setTimeout(() => {
          setLastFieldStats(null);
          fieldStatsTimerRef.current = null;
        }, 5000);
      }
    } finally {
      setRegeneratingField(null);
      regenInFlightRef.current = false;
    }
  }, [onRegenerateField, readOnlySet, onEdit, word]);

  const handleUndoRegen = useCallback((fieldId: string) => {
    const prev = undoValues[fieldId];
    if (prev === undefined || !onEdit) return;
    onEdit(fieldId, prev);
    setUndoValues((u) => {
      const next = { ...u };
      delete next[fieldId];
      return next;
    });
  }, [undoValues, onEdit]);

  return (
    <div className="stage-view">
      {!hasContent && (
        <div className="stage-generate-area">
          <button className="generate-btn" onClick={onGenerate} disabled={generating} title={`Generate ${config.label} (\u2318G)`}>
            {generating ? 'Generating...' : `Generate ${config.label}`}
          </button>
        </div>
      )}

      {hasContent && (
        <div className="stage-toolbar">
          <button className="regenerate-btn" onClick={onGenerate} disabled={generating} title="Regenerate (\u2318R)">
            {generating ? 'Regenerating...' : 'Regenerate'}
          </button>
          <button
            className="compare-btn"
            onClick={compareData ? handleDismissCompare : handleCompare}
            disabled={generating}
          >
            {compareData ? 'Exit Compare' : 'Compare'}
          </button>
          <CompactToggle />
          {taskName && (
            <label className="qa-toggle">
              <input
                type="checkbox"
                checked={qaEnabled}
                onChange={(e) => toggleQuickApprove(taskName, e.target.checked)}
              />
              <span>Quick Approve</span>
            </label>
          )}
          {toolbarModel && (
            <span className="stage-model-label" title={lastGenStats ? 'Latest generation this session' : 'Model that produced the stored content'}>
              <strong>{toolbarModel}</strong>
              {lastGenStats && (
                <>
                  <span className="stage-model-sep"> · </span>
                  {formatTokens(lastGenStats.tokens)}
                  <span className="stage-model-sep"> · </span>
                  {lastGenStats.elapsedSec}s
                  {lastGenStats.promptFile && (
                    <>
                      <span className="stage-model-sep"> · </span>
                      <span title="Canonical prompt file this batch was generated from">
                        {lastGenStats.promptFile}{lastGenStats.promptVersion ? ` (${lastGenStats.promptVersion})` : ''}
                      </span>
                    </>
                  )}
                </>
              )}
            </span>
          )}
        </div>
      )}

      {lintFindings.length > 0 && (
        <div className="qa-lint-panel">
          <span className="qa-lint-title">QA flags ({lintFindings.length})</span>
          <ul className="qa-lint-list">
            {lintFindings.map((f, i) => (
              <li key={`${f.field}-${i}`} className={`qa-lint-item qa-lint-${f.severity}`}>
                <strong>{formatFieldLabel(f.field)}</strong>: {f.message}{f.match ? ` — "${f.match}"` : ''}
              </li>
            ))}
          </ul>
        </div>
      )}

      {hasContent && qaEnabled && !compareData && (
        <button className="qa-action-btn" onClick={() => onApprove({})} disabled={saving}>
          {saving ? 'Approving...' : 'Quick Approve'}
        </button>
      )}

      {showNudge && !qaEnabled && hasContent && (
        <div className="qa-nudge">
          <span>You've approved the last 20 {config.label} outputs without editing. Enable Quick Approve for this stage?</span>
          <div className="qa-nudge-actions">
            <button className="qa-nudge-enable" onClick={() => toggleQuickApprove(taskName!, true)}>Enable</button>
            <button className="qa-nudge-dismiss" onClick={() => dismissNudge(taskName!)}>Not now</button>
          </div>
        </div>
      )}

      {hasContent && !compareData && (
        <div className="review-groups">
          {(config.fieldGroups || []).map((group) => {
            return (
              <div key={group.title} className="review-group">
                <h3 className="review-group-title">{group.title}</h3>
                <div className="review-group-cards">
                  {group.fields.map((field) => {
                    const isReadOnly = readOnlySet.has(field);
                    const showStats = lastFieldStats && lastFieldStats.field === field;
                    return (
                      <div key={field} className="review-card-wrap">
                      <ContentCard
                        id={field}
                        content={String(word[field] || '')}
                        label={formatFieldLabel(field)}
                        showCheckbox={false}
                        onEdit={handleEdit}
                        compact={compactCards}
                        readOnly={isReadOnly}
                        onRegenerate={onRegenerateField && !isReadOnly ? handleRegenerateInline : undefined}
                        onUndo={!isReadOnly && undoValues[field] !== undefined ? () => handleUndoRegen(field) : undefined}
                        regenerating={regeneratingField === field}
                        regenStats={showStats ? { tokens: lastFieldStats.tokens, elapsedSec: lastFieldStats.elapsedSec } : null}
                        emptyPlaceholder="Not generated yet"
                      />
                      {/* Stage 1 only: promote a proposed related word to a Game
                          word of its own and refill the slot. */}
                      {group.title === 'Related Words' && /^related_word_[123]$/.test(field) && !!word[field] && (
                        <RelatedSlotActions
                          parentId={String(word.id)}
                          slot={Number(field.slice(-1)) as 1 | 2 | 3}
                          word={String(word[field])}
                          onSlotChanged={onSlotChanged ?? (() => {})}
                        />
                      )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}

          <ApproveButton
            onClick={() => onApprove({})}
            disabled={saving}
            loading={saving}
          />
        </div>
      )}

      {hasContent && compareData && (
        <div className="compare-mode">
          <div className="compare-review-header">
            <div className="compare-model-bar">
              <span className="compare-model-side">
                Current{persistedModel ? <> · <strong>{persistedModel}</strong></> : null}
              </span>
              <span className="compare-model-side compare-model-new">
                New{compareModel ? <> · <strong>{compareModel}</strong></> : null}
              </span>
            </div>
            <button className="compare-accept-all" onClick={handleAcceptAllNew}>
              Accept all new
            </button>
          </div>

          <div className="review-groups">
            {(config.fieldGroups || []).map((group) => {
              const filledFields = group.fields.filter((f) => word[f] || compareData[f]);
              if (filledFields.length === 0) return null;

              return (
                <div key={group.title} className="review-group">
                  <h3 className="review-group-title">{group.title}</h3>
                  {filledFields.map((field) => {
                    const isReadOnly = readOnlySet.has(field);
                    const newValue = compareData[field] || '';
                    const differs = newValue && newValue !== word[field];
                    return (
                      <div key={field} className="compare-field-row">
                        <div className="compare-field-pair">
                          <div className="compare-field-side">
                            <span className="compare-field-tag">Current</span>
                            <EditableField
                              value={String(word[field] || '')}
                              onChange={(v) => handleEdit(field, v)}
                              readOnly={isReadOnly}
                            />
                          </div>
                          <div className="compare-field-side compare-field-new">
                            <div className="compare-field-tag-row">
                              <span className="compare-field-tag">New</span>
                              {onRegenerateField && !isReadOnly && (
                                <button
                                  className="compare-regen-field"
                                  onClick={() => handleRegenerateOne(field)}
                                  disabled={regeneratingField === field || generating}
                                  title="Regenerate just this field"
                                >
                                  {regeneratingField === field ? 'Regenerating...' : 'Regenerate'}
                                </button>
                              )}
                            </div>
                            <EditableField
                              value={newValue}
                              onChange={(v) => handleEditCompareField(field, v)}
                              readOnly={isReadOnly}
                              placeholder={isReadOnly ? '' : 'Click Regenerate or type here…'}
                            />
                            {!isReadOnly && differs && (
                              <button className="compare-use-new" onClick={() => handleUseNew(field)}>
                                Use new
                              </button>
                            )}
                          </div>
                        </div>
                        <div className="compare-field-label">{formatFieldLabel(field)}</div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>

          <ApproveButton
            onClick={() => onApprove({})}
            disabled={saving}
            loading={saving}
          />
        </div>
      )}

      {generating && !hasContent && (
        <div className="stage-generating">
          <div className="generating-spinner" />
          <p>Generating {config.label}...</p>
        </div>
      )}
    </div>
  );
}

function EditableField({ value, onChange, readOnly, placeholder }: { value: string; onChange: (v: string) => void; readOnly: boolean; placeholder?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (ref.current && !focused) {
      ref.current.textContent = value;
    }
  }, [value, focused]);

  return (
    <div
      ref={ref}
      className={`compare-field-content content-text ${readOnly ? 'compare-field-readonly' : 'compare-field-editable'}`}
      contentEditable={!readOnly}
      suppressContentEditableWarning
      data-placeholder={placeholder || ''}
      onFocus={() => setFocused(true)}
      onBlur={() => {
        setFocused(false);
        const next = ref.current?.textContent ?? '';
        if (next !== value) onChange(next);
      }}
      spellCheck={false}
    />
  );
}

function formatFieldLabel(fieldName: string): string {
  return fieldName
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}
